import { actionProps } from "@/lib/controlAnalytics";
import { DownloadIcon } from "@/components/icons";
import { shortHash } from "@/lib/applications";
import { formatBytes } from "@/lib/format";
import { useModels } from "@/lib/hooks/gateway";
import { useOpenTailored, useTailoredLinks } from "@/lib/hooks/resume";
import { stamp, type WrittenResume } from "@/lib/posting";
import { PdfTile } from "./parts";

/** The resumes written for this posting, the newest first. */
export default function ResumesWritten({ written }: { written: readonly WrittenResume[] }) {
    const links = useTailoredLinks(written.map((entry) => entry.resumeId));
    const models = useModels();
    const openPdf = useOpenTailored();
    // The catalog's name for the model, never its provider or its id; null when it is not listed.
    const nameOf = (model: string | null) =>
        (model && models.data?.models.find((entry) => entry.model === model)?.display_name) || null;

    return (
        <div className="flex flex-col">
            {written.map((entry) => {
                const link = links[entry.resumeId];
                const meta = [
                    entry.tailor === "keyword" ? "keyword" : nameOf(entry.model),
                    link ? formatBytes(link.byte_size) : null,
                    link ? `sha256 ${shortHash(link.sha256)}` : null,
                    stamp(entry.at),
                ]
                    .filter(Boolean)
                    .join(" · ");
                return (
                    <div
                        key={entry.resumeId}
                        className="flex flex-wrap items-center gap-3 rounded-lg border-t border-line-soft px-2 py-2.5 transition-colors hover:bg-hover"
                    >
                        <PdfTile />
                        <span className="flex min-w-0 flex-[1_1_200px] flex-col gap-0.5">
                            <span className="font-semibold">Tailored resume</span>
                            <span className="font-mono text-small text-fg-faint">{meta}</span>
                        </span>
                        {entry.latest ? (
                            <span className="inline-flex items-center rounded-full bg-applied px-[9px] py-[3px] text-small font-semibold text-applied-fg">
                                Latest
                            </span>
                        ) : (
                            <span className="inline-flex items-center rounded-full bg-card-raised px-[9px] py-[3px] text-small font-medium text-fg-muted">
                                Superseded
                            </span>
                        )}
                        <button
                            {...actionProps("tailored_pdf_open_clicked", {
                                source: "ResumesWritten",
                                resume_id: entry.resumeId,
                            })}
                            type="button"
                            onClick={() => openPdf.mutate(entry.resumeId)}
                            className="inline-flex h-8 max-[760px]:min-h-11 items-center gap-[5px] rounded-[9px] border border-line-strong px-3 text-small font-semibold text-fg transition-colors hover:bg-hover"
                        >
                            <DownloadIcon className="size-[13px]" />
                            Download
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
